import { useState, useEffect } from 'react';
import { PlaylistSong, EnrichedSong } from '../../../types/PlaylistTypes';
import { enrichSongs } from '../utils/playlistApi';

export const useSongSelection = (initialSongs: PlaylistSong[] = []) => {
    const [selectedSongs, setSelectedSongs] = useState<PlaylistSong[]>(initialSongs);
    const [enrichedSongs, setEnrichedSongs] = useState<EnrichedSong[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Enrich songs whenever selected songs change
    useEffect(() => {
        const enrichSelectedSongs = async () => {
            if (!selectedSongs.length) {
                setEnrichedSongs([]);
                return;
            }

            setIsLoading(true);
            try {
                const enrichedData = await enrichSongs(selectedSongs);
                setEnrichedSongs(enrichedData);
            } catch (err) {
                console.error("Error enriching songs:", err);
            } finally {
                setIsLoading(false);
            }
        };

        enrichSelectedSongs();
    }, [selectedSongs]);

    const addSong = (song: PlaylistSong) => {
        const songId = song.melon_song_id || song.id;
        if (!songId) {
            console.error('Invalid song object:', song);
            return;
        }

        // Check if song is already selected
        const alreadySelected = selectedSongs.some(s => {
            const existingId = s.melon_song_id || s.id;
            return existingId === songId;
        });

        if (!alreadySelected) {
            const newSong = {
                ...song,
                melon_song_id: songId,
                id: undefined // Remove the id field to avoid confusion
            };
            setSelectedSongs(prev => [...prev, newSong]);
        }
    };

    const removeSong = (songId: number) => {
        setSelectedSongs(prev => prev.filter(song => song.melon_song_id !== songId));
    };

    const reorderSongs = (songs: PlaylistSong[]) => {
        setSelectedSongs(songs);
    };

    const clearSongs = () => {
        setSelectedSongs([]);
    };

    const setSongs = (songs: PlaylistSong[]) => {
        setSelectedSongs(songs);
    };

    return {
        selectedSongs,
        enrichedSongs,
        addSong,
        removeSong,
        reorderSongs,
        clearSongs,
        setSongs,
        isLoading
    };
}; 